// src/lib/resources/data/generators/subtitleOverlayGenerator.ts

import { 
  ToolType, 
  ToolCategories, 
  DifficultyLevel
} from '~/lib/resources/types/shared/enums'
import type { Generator } from '~/lib/resources/types/tools'

export const subtitleOverlayGenerator: Generator = {
  id: 'subtitle-overlay-generator',
  title: 'Subtitle Overlay Mockup Generator',
  canonicalTitle: 'Subtitle Overlay Mockup Generator',
  description: 'Create professional subtitle overlay mockups for marketing videos, presentations, and social media content. Perfect for showcasing video concepts, A/B testing subtitle designs, and creating marketing materials. Customize text, styling, positioning, and background effects to match your brand.',
  resourceType: 'tool',
  toolType: ToolType.GENERATOR,
  toolCategory: ToolCategories.CREATIVE,
  slug: 'subtitle-overlay-generator',
  category: 'Design Tools',
  featured: true,
  lastUpdated: '2026-05-29T13:13:26+00:00',
  estimatedTime: '5-10 min',
  difficulty: DifficultyLevel.BEGINNER,
  
  // Images
  images: {
    hero: {
      url: '/images/resources/tools/generators/adsights-subtitle-overlay-generator-design-tools-marketing-tools-hero-image_AR-16x9_2400x1350_2x.jpg',
      width: 2400,
      height: 1350,
      alt: 'Subtitle Overlay Mockup Generator tool showing customizable text overlays for video mockups and marketing content'
    },
    thumbnail: {
      url: '/images/resources/tools/generators/adsights-subtitle-overlay-generator-design-tools-marketing-tools-hero-image_AR-16x9_1200x675_1x.jpg',
      width: 1200,
      height: 675,
      alt: 'Subtitle Overlay Mockup Generator tool showing customizable text overlays for video mockups and marketing content'
    },
    og: {
      url: '/images/resources/tools/generators/adsights-subtitle-overlay-generator-design-tools-marketing-tools-og-image_AR-1x1.91_2400x1260_2x.jpg',
      width: 2400,
      height: 1260,
      alt: 'Subtitle Overlay Mockup Generator tool showing customizable text overlays for video mockups and marketing content'
    }
  },
  
  // Related resources
  relatedTools: [
    'creative-quality-grader', // Creative Quality Grader
    'roas-calculator', // ROAS Calculator
    'video-dropoff-calculator', // Video Drop-off Rate Calculator
    'ab-test-statistical-significance-calculator', // A/B Test Statistical Significance Calculator
  ],
  
  // Metadata
  metadata: {
    title: 'Subtitle Overlay Generator | Free Video Text Overlay Tool',
    description: 'Generate custom subtitle overlay mockups for marketing videos, presentations, and social media content. Free tool to create professional text overlays with customizable styling, positioning, and effects.',
    keywords: [
      'subtitle overlay mockup generator',
      'video subtitle tool',
      'marketing screenshot tool',
      'marketing screenshot subtitle',
      'text overlay creator',
      'video text generator',
      'marketing mockup tool',
      'social media video',
      'social media screenshot',
      'video caption generator',
      'screenshot caption generator',   
      'text overlay design',
      'video subtitle maker',
      'video subtitle screenshot',
      'marketing content tool',
      'video editing tool',
      'video subtitle mockup generator',
      'subtitle design',
      'video text overlay',
      'caption generator',
      'marketing video tool',
      'content creation tool',
      'video subtitle creator',
      'text overlay maker',
      'video caption tool',
      'video captioning screenshot',
      'marketing video subtitle'
    ],
    dateCreated: '2025-09-30T12:00:00Z',
    dateModified: '2026-05-29T13:13:26+00:00',
    canonicalURL: '/resources/tools/generators/subtitle-overlay-generator'
  },

  // Structured data (Schema.org)
  teaches: 'Video Subtitle Design, Text Overlay Creation, Marketing Video Production, Content Design',
  assesses: 'Subtitle Design Skills, Video Content Creation, Marketing Asset Development',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Marketing Design Tool',
  featureList: [
    'Interactive subtitle overlay builder with customizable text styling',
    'Positioning controls',
    'Background effects',
    'Color schemes',
    'Font selection',
    // Both Download paths verified against SubtitleOverlayGenerator.tsx:
    // `downloadPNG` (line 1416-1470) + sibling `downloadOverlay`
    // (line 353-405) wire the user-facing "Download PNG" button;
    // `downloadHTML` (line 1387-1410) wires the secondary
    // "Download HTML" button. Both are real, working exports.
    'PNG download (html2canvas) + secondary HTML download',
  ],
  interactivityType: 'active',
  softwareRequirements: 'Modern web browser with JavaScript enabled',
  browserRequirements: 'Chrome 80+, Firefox 75+, Safari 13.1+, or Edge 80+',
  
  // Stats
  stats: {
    views: 0,
    completions: 0,
    shares: 0
  },
  
  config: {
    steps: [
      // Step 1: Content & Typography
      {
        id: 'content-typography',
        title: 'Content & Typography',
        description: 'Enter your subtitle text and customize the typography settings.',
        fields: [
          {
            id: 'subtitleText',
            type: 'textarea',
            label: 'Subtitle Text',
            description: 'Enter the text you want to display as a subtitle overlay on your video mockup.',
            placeholder: 'Enter your subtitle text here...',
            validation: {
              required: true
            },
            templateVar: 'subtitleText',
            affectsPreview: true
          },
          {
            id: 'textAlignment',
            type: 'select',
            label: 'Text Alignment',
            description: 'How should the text be aligned within the overlay?',
            validation: {
              required: true
            },
            templateVar: 'textAlignment',
            affectsPreview: true,
            defaultValue: 'center',
            options: [
              { id: 'left', label: 'Left', value: 'left' },
              { id: 'center', label: 'Center', value: 'center' },
              { id: 'right', label: 'Right', value: 'right' }
            ]
          },
          {
            id: 'maxWidth',
            type: 'number',
            label: 'Maximum Width (%)',
            description: 'Maximum width of the subtitle overlay as a percentage of the video width.',
            validation: {
              required: true,
              enhancedMin: {
                value: 20,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 100,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 1,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'slider'
            },
            templateVar: 'maxWidth',
            affectsPreview: true,
            defaultValue: 90
          },
          {
            id: 'fontFamily',
            type: 'select',
            label: 'Font Family',
            description: 'Choose a font that matches your brand and content style.',
            validation: {
              required: true
            },
            templateVar: 'fontFamily',
            affectsPreview: true,
            defaultValue: 'Arial, sans-serif',
            options: [
              { id: 'arial', label: 'Arial', value: 'Arial, sans-serif' },
              { id: 'helvetica', label: 'Helvetica', value: 'Helvetica, sans-serif' },
              { id: 'georgia', label: 'Georgia', value: 'Georgia, serif' },
              { id: 'times', label: 'Times New Roman', value: 'Times New Roman, serif' },
              { id: 'verdana', label: 'Verdana', value: 'Verdana, sans-serif' },
              { id: 'trebuchet', label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
              { id: 'impact', label: 'Impact', value: 'Impact, sans-serif' },
              { id: 'comic', label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' }
            ]
          },
          {
            id: 'fontSize',
            type: 'number',
            label: 'Font Size (px)',
            description: 'Size of the subtitle text in pixels.',
            validation: {
              required: true,
              enhancedMin: {
                value: 12,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 72,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 1,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'slider'
            },
            templateVar: 'fontSize',
            affectsPreview: true,
            defaultValue: 18
          },
          {
            id: 'fontWeight',
            type: 'select',
            label: 'Font Weight',
            description: 'How bold should the text appear?',
            validation: {
              required: true
            },
            templateVar: 'fontWeight',
            affectsPreview: true,
            defaultValue: 'normal',
            options: [
              { id: 'normal', label: 'Normal', value: 'normal' },
              { id: 'bold', label: 'Bold', value: 'bold' },
              { id: 'lighter', label: 'Lighter', value: 'lighter' },
              { id: 'bolder', label: 'Bolder', value: 'bolder' }
            ]
          },
          {
            id: 'lineHeight',
            type: 'number',
            label: 'Line Height',
            description: 'Spacing between lines of text (1.0 = normal, 1.5 = 1.5x spacing).',
            validation: {
              required: true,
              enhancedMin: {
                value: 0.8,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 3.0,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 0.01,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'stepper',
              stepperConfig: {
                step: 0.1,
                min: 0.8,
                max: 3.0,
                fastIncrement: true,
                fastIncrementDelay: 150
              }
            },
            templateVar: 'lineHeight',
            affectsPreview: true,
            defaultValue: 1.4
          }
        ],
        template: `
          <div class="subtitle-overlay" style="max-width: {{maxWidth}}%; text-align: {{textAlignment}};">
            <div class="subtitle-text" style="font-family: {{fontFamily}}; font-size: {{fontSize}}px; font-weight: {{fontWeight}}; line-height: {{lineHeight}};">{{subtitleText}}</div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="subtitle-preview" style="max-width: {{maxWidth}}%; text-align: {{textAlignment}};">
              <p class="preview-text" style="font-family: {{fontFamily}}; font-size: {{fontSize}}px; font-weight: {{fontWeight}}; line-height: {{lineHeight}};">{{subtitleText}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 2: Colors & Effects
      {
        id: 'colors-effects',
        title: 'Colors & Effects',
        description: 'Set text color, background, and visual effects.',
        fields: [
          {
            id: 'textColor',
            type: 'color',
            label: 'Text Color',
            description: 'Color of the subtitle text.',
            validation: {
              required: true
            },
            templateVar: 'textColor',
            affectsPreview: true,
            defaultValue: '#ffffff'
          },
          {
            id: 'backgroundColor',
            type: 'color',
            label: 'Background Color',
            description: 'Color of the subtitle background.',
            validation: {
              required: true
            },
            templateVar: 'backgroundColor',
            affectsPreview: true,
            defaultValue: '#000000'
          },
          {
            id: 'backgroundOpacity',
            type: 'number',
            label: 'Background Opacity',
            description: 'Transparency of the background (0 = transparent, 1 = opaque).',
            validation: {
              required: true,
              enhancedMin: {
                value: 0,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 1,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 0.01,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'stepper',
              stepperConfig: {
                step: 0.1,
                min: 0,
                max: 1,
                fastIncrement: true,
                fastIncrementDelay: 100
              }
            },
            templateVar: 'backgroundOpacity',
            affectsPreview: true,
            defaultValue: 0.75
          },
          {
            id: 'textShadow',
            type: 'select',
            label: 'Text Shadow',
            description: 'Add a shadow effect to make text more readable.',
            validation: {
              required: true
            },
            templateVar: 'textShadow',
            affectsPreview: true,
            options: [
              { id: 'none', label: 'None', value: 'none' },
              { id: 'subtle', label: 'Subtle', value: '1px 1px 2px rgba(0,0,0,0.8)' },
              { id: 'medium', label: 'Medium', value: '2px 2px 4px rgba(0,0,0,0.8)' },
              { id: 'strong', label: 'Strong', value: '3px 3px 6px rgba(0,0,0,0.8)' }
            ]
          },
          {
            id: 'borderRadius',
            type: 'number',
            label: 'Border Radius (px)',
            description: 'Roundness of the subtitle background corners.',
            validation: {
              required: true,
              enhancedMin: {
                value: 0,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 20,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 1,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'stepper',
              stepperConfig: {
                step: 1,
                min: 0,
                max: 20,
                fastIncrement: true,
                fastIncrementDelay: 150
              }
            },
            templateVar: 'borderRadius',
            affectsPreview: true,
            defaultValue: 4
          }
        ],
        template: `
          <div class="subtitle-overlay">
            <div class="subtitle-text" style="color: {{textColor}}; background-color: {{backgroundColor}}; opacity: {{backgroundOpacity}}; text-shadow: {{textShadow}}; border-radius: {{borderRadius}}px;">{{subtitleText}}</div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="subtitle-preview">
              <p class="preview-text" style="color: {{textColor}}; background-color: {{backgroundColor}}; opacity: {{backgroundOpacity}}; text-shadow: {{textShadow}}; border-radius: {{borderRadius}}px; padding: 8px 16px;">{{subtitleText}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 3: Positioning & Background
      {
        id: 'positioning-background',
        title: 'Positioning & Background',
        description: 'Control where the subtitle appears and customize the video background.',
        fields: [
          {
            id: 'position',
            type: 'select',
            label: 'Position',
            description: 'Where should the subtitle be positioned on the video?',
            validation: {
              required: true
            },
            templateVar: 'position',
            affectsPreview: true,
            defaultValue: 'bottom-center',
            options: [
              { id: 'bottom-center', label: 'Bottom Center', value: 'bottom-center' },
              { id: 'bottom-left', label: 'Bottom Left', value: 'bottom-left' },
              { id: 'bottom-right', label: 'Bottom Right', value: 'bottom-right' },
              { id: 'top-center', label: 'Top Center', value: 'top-center' },
              { id: 'top-left', label: 'Top Left', value: 'top-left' },
              { id: 'top-right', label: 'Top Right', value: 'top-right' },
              { id: 'center', label: 'Center', value: 'center' },
              { id: 'center-left', label: 'Center Left', value: 'center-left' },
              { id: 'center-right', label: 'Center Right', value: 'center-right' }
            ]
          },
          {
            id: 'margin',
            type: 'number',
            label: 'Margin from Edge (px)',
            description: 'Distance from the edge of the video frame.',
            validation: {
              required: true,
              enhancedMin: {
                value: 10,
                applyToText: true,
                applyToStepper: true
              },
              enhancedMax: {
                value: 100,
                applyToText: true,
                applyToStepper: true
              },
              stepConstraint: {
                value: 1,
                applyToModes: ['text']
              }
            },
            inputModes: {
              primary: 'number',
              secondary: 'stepper',
              stepperConfig: {
                step: 5,
                min: 10,
                max: 100,
                fastIncrement: true,
                fastIncrementDelay: 100
              }
            },
            templateVar: 'margin',
            affectsPreview: true,
            defaultValue: 40
          },
          {
            id: 'showPlayerControls',
            type: 'checkbox',
            label: 'Show Video Player Controls',
            description: 'Include video player controls in the mockup for context.',
            validation: {
              required: false
            },
            templateVar: 'showPlayerControls',
            affectsPreview: true,
            defaultValue: true
          },
          {
            id: 'backgroundType',
            type: 'select',
            label: 'Background Type',
            description: 'What type of background should be used for the mockup?',
            validation: {
              required: true
            },
            templateVar: 'backgroundType',
            affectsPreview: true,
            defaultValue: 'gradient',
            options: [
              { id: 'gradient', label: 'Gradient', value: 'gradient' },
              { id: 'solid', label: 'Solid Color', value: 'solid' },
              { id: 'upload', label: 'Upload Image', value: 'upload' }
            ]
          },
          {
            id: 'gradientType',
            type: 'select',
            label: 'Gradient Style',
            description: 'Choose a gradient style for the background.',
            validation: {
              required: false,
              requiredIf: {
                field: 'backgroundType',
                operator: 'equals',
                value: 'gradient'
              }
            },
            templateVar: 'gradientType',
            affectsPreview: true,
            defaultValue: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            showIf: {
              field: 'backgroundType',
              operator: 'equals',
              value: 'gradient'
            },
            options: [
              { id: 'blue-purple', label: 'Blue to Purple', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
              { id: 'pink-red', label: 'Pink to Red', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
              { id: 'blue-cyan', label: 'Blue to Cyan', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
              { id: 'green-teal', label: 'Green to Teal', value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
              { id: 'orange-red', label: 'Orange to Red', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
              { id: 'purple-pink', label: 'Purple to Pink', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }
            ]
          },
          {
            id: 'solidColor',
            type: 'color',
            label: 'Solid Background Color',
            description: 'Choose a solid color for the background.',
            validation: {
              required: false,
              requiredIf: {
                field: 'backgroundType',
                operator: 'equals',
                value: 'solid'
              }
            },
            templateVar: 'solidColor',
            affectsPreview: true,
            showIf: {
              field: 'backgroundType',
              operator: 'equals',
              value: 'solid'
            },
            defaultValue: '#2c3e50'
          },
          {
            id: 'backgroundImage',
            type: 'file',
            label: 'Background Image',
            description: 'Upload a custom background image for your video subtitle mockup.',
            validation: {
              required: false,
              requiredIf: {
                field: 'backgroundType',
                operator: 'equals',
                value: 'upload'
              }
            },
            templateVar: 'backgroundImage',
            affectsPreview: true,
            showIf: {
              field: 'backgroundType',
              operator: 'equals',
              value: 'upload'
            },
            accept: 'image/*',
            maxFileSize: 5 * 1024 * 1024, // 5MB limit
            allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
            securityValidation: {
              scanForMalware: true,
              validateFileHeaders: true,
              maxImageDimensions: {
                width: 4000,
                height: 4000
              },
              allowedMimeTypes: [
                'image/jpeg',
                'image/jpg', 
                'image/png',
                'image/webp',
                'image/gif'
              ]
            }
          }
        ],
        template: `
          <div class="subtitle-overlay" data-position="{{position}}" data-margin="{{margin}}" data-background-type="{{backgroundType}}" data-gradient="{{gradientType}}" data-solid-color="{{solidColor}}" data-background-image="{{backgroundImage}}">
            <div class="subtitle-text">{{subtitleText}}</div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="subtitle-preview" data-position="{{position}}" data-margin="{{margin}}" data-background-type="{{backgroundType}}" data-gradient="{{gradientType}}" data-solid-color="{{solidColor}}" data-background-image="{{backgroundImage}}">
              <p class="preview-text">{{subtitleText}}</p>
            </div>
          `,
          format: 'html'
        }
      }
    ],
    output: {
      // SubtitleOverlay's user-visible Download UI ships TWO
      // working export paths (verified against
      // `SubtitleOverlayGenerator.tsx`):
      //   1. PNG via `downloadPNG` (line 1416-1470) and the
      //      sibling `downloadOverlay` (line 353-405) — both call
      //      html2canvas → `canvas.toBlob` → `<a download>.png`.
      //      The user-facing "Download PNG" button (line 1527)
      //      is the primary capture path.
      //   2. HTML via `downloadHTML` (line 1387-1410) — emits a
      //      standalone HTML file with the styled overlay. The
      //      "Download HTML" button (line 1521) is the secondary
      //      path for embed use cases.
      // Primary `output.format` declares PNG to match the primary
      // user-facing path; HTML is declared as a variant so the
      // data record reflects both. The previous data record
      // declared only `text/html` as the primary, which produced
      // a misleading JSON-LD `CreateAction.result.encodingFormat`
      // for the more common PNG download. From PR
      // #104 truth-table audit.
      format: {
        type: 'image',
        encodingFormat: 'image/png'
      },
      variants: [
        {
          condition: {
            field: 'exportFormat',
            operator: 'equals',
            value: 'html'
          },
          format: {
            type: 'html',
            encodingFormat: 'text/html'
          },
          template: 'subtitle-overlay-html-template',
          filename: 'subtitle-overlay.html'
        }
      ],
      template: `
        <div class="subtitle-overlay-document">
          <div class="overlay-header">
            <h1>Subtitle Overlay Mockup Design</h1>
            <p class="overlay-description">Custom subtitle overlay for video mockups</p>
          </div>

          <div class="overlay-preview">
            <div class="video-mockup" style="
              width: 800px; 
              height: 450px; 
              background: {{#if backgroundType == 'gradient'}}{{gradientType}}{{else if backgroundType == 'solid'}}{{solidColor}}{{else}}#000000{{/if}};
              {{#if backgroundType == 'upload'}}background-image: url('{{backgroundImage}}'); background-size: cover; background-position: center;{{/if}}
              position: relative;
              border-radius: 10px;
              overflow: hidden;
              box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            ">
              <div class="subtitle-overlay" style="
                position: absolute;
                {{#if position == 'bottom-center'}}bottom: {{margin}}px; left: 50%; transform: translateX(-50%);{{/if}}
                {{#if position == 'bottom-left'}}bottom: {{margin}}px; left: {{margin}}px;{{/if}}
                {{#if position == 'bottom-right'}}bottom: {{margin}}px; right: {{margin}}px;{{/if}}
                {{#if position == 'top-center'}}top: {{margin}}px; left: 50%; transform: translateX(-50%);{{/if}}
                {{#if position == 'top-left'}}top: {{margin}}px; left: {{margin}}px;{{/if}}
                {{#if position == 'top-right'}}top: {{margin}}px; right: {{margin}}px;{{/if}}
                {{#if position == 'center'}}top: 50%; left: 50%; transform: translate(-50%, -50%);{{/if}}
                {{#if position == 'center-left'}}top: 50%; left: {{margin}}px; transform: translateY(-50%);{{/if}}
                {{#if position == 'center-right'}}top: 50%; right: {{margin}}px; transform: translateY(-50%);{{/if}}
                max-width: {{maxWidth}}%;
                text-align: {{textAlignment}};
              ">
                <div class="subtitle-text" style="
                  font-family: {{fontFamily}};
                  font-size: {{fontSize}}px;
                  font-weight: {{fontWeight}};
                  line-height: {{lineHeight}};
                  color: {{textColor}};
                  background-color: {{backgroundColor}};
                  opacity: {{backgroundOpacity}};
                  text-shadow: {{textShadow}};
                  border-radius: {{borderRadius}}px;
                  padding: 8px 16px;
                  display: inline-block;
                ">{{subtitleText}}</div>
              </div>
              
              {{#if showPlayerControls}}
              <div class="player-controls" style="
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background: linear-gradient(transparent, rgba(0,0,0,0.8));
                padding: 20px 15px 15px;
                display: flex;
                flex-direction: column;
                gap: 10px;
              ">
                <div class="progress-bar" style="
                  width: 100%;
                  height: 4px;
                  background: rgba(255,255,255,0.3);
                  border-radius: 2px;
                  position: relative;
                ">
                  <div class="progress-fill" style="
                    height: 100%;
                    background: #ff0000;
                    border-radius: 2px;
                    width: 35%;
                  "></div>
                </div>
                <div class="controls-row" style="
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  color: white;
                  font-size: 14px;
                ">
                  <div class="controls-left" style="display: flex; align-items: center; gap: 15px;">
                    <button style="width: 24px; height: 24px; border: none; background: none; color: white; cursor: pointer;">▶</button>
                    <span>0:35 / 2:14</span>
                  </div>
                  <div class="controls-right" style="display: flex; align-items: center; gap: 15px;">
                    <div class="volume-control" style="display: flex; align-items: center; gap: 5px;">
                      <span>🔊</span>
                      <div class="volume-bar" style="width: 60px; height: 3px; background: rgba(255,255,255,0.3); border-radius: 2px;">
                        <div class="volume-fill" style="height: 100%; background: white; border-radius: 2px; width: 70%;"></div>
                      </div>
                    </div>
                    <button style="width: 20px; height: 20px; border: 1px solid white; background: none; cursor: pointer;"></button>
                  </div>
                </div>
              </div>
              {{/if}}
            </div>
          </div>

          <div class="overlay-specifications">
            <h2>Design Specifications</h2>
            <div class="spec-grid">
              <div class="spec-item">
                <span class="spec-label">Text:</span>
                <span class="spec-value">{{subtitleText}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Font:</span>
                <span class="spec-value">{{fontFamily}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Size:</span>
                <span class="spec-value">{{fontSize}}px</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Weight:</span>
                <span class="spec-value">{{fontWeight}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Text Color:</span>
                <span class="spec-value" style="color: {{textColor}};">{{textColor}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Background:</span>
                <span class="spec-value" style="background-color: {{backgroundColor}}; color: white; padding: 2px 6px; border-radius: 3px;">{{backgroundColor}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Position:</span>
                <span class="spec-value">{{position}}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">Alignment:</span>
                <span class="spec-value">{{textAlignment}}</span>
              </div>
            </div>
          </div>

          <div class="overlay-footer">
            <p>Generated with AdSights Subtitle Overlay Mockup Generator | {{formatDate now 'YYYY-MM-DD'}}</p>
          </div>
        </div>
      `,
      filename: 'subtitle-overlay-mockup-{{formatDate now "YYYY-MM-DD"}}.html'
    },
    ui: {
      enablePreview: true,
      enableDownload: true,
      previewDelay: 300
    }
  },
  faqs: [
    {
      question: "What is a subtitle overlay mockup and why is it important for marketing videos?",
      answer: "A subtitle overlay mockup is a visual representation of how text will appear on video content, used to create marketing assets and promotional materials. For marketing videos, subtitle overlay mockups are crucial because they help create compelling social media posts and ads before video production, allow you to test different messaging and visual styles, generate thumbnail previews and promotional graphics, create consistent branding across video marketing materials, help stakeholders visualize the final product during planning phases, and enable rapid iteration of text positioning and styling. They're particularly valuable for creating marketing assets that showcase your video content with professional-looking subtitle overlays."
    },
    {
      question: "How do I customize the appearance of my subtitle overlay?",
      answer: "The generator provides several customization options: choose your subtitle text content, select font family and size for readability, adjust font weight (normal, bold, etc.), set text color for visibility, add background color behind text for contrast, position the subtitle (top, center, bottom), and align text (left, center, right). Experiment with different combinations to find the style that works best for your content and branding needs. Best practices for subtitles tend to follow several key principles: Use high contrast between text and background colors, choose legible fonts at appropriate sizes, keep text concise and easy to scan, position subtitles where they won't obscure important visual content, maintain consistency with your brand colors and fonts, and ensure the design works well when scaled down for thumbnails or social media previews."
    },
    {
      question: "What makes a good subtitle overlay design?",
      answer: "Effective subtitle overlays prioritize readability and visual appeal. Use high contrast between text and background colors, choose legible fonts at appropriate sizes, keep text concise and easy to scan, position subtitles where they won't obscure important visual content, maintain consistency with your brand colors and fonts, and ensure the design works well when scaled down for thumbnails or social media previews."
    },
    {
      question: "How can I use the generated mockup files?",
      answer: "The generator creates HTML files that you can open in any web browser to view your subtitle overlay mockup as well as a downloadable PNG file. You can take screenshots of the mockup for use in presentations, marketing materials, or social media posts. The HTML format makes it easy to share with team members or clients for approval, and you can modify the generated code if you need to make additional customizations."
    },
    {
      question: "How can I use subtitle overlay mockups for different marketing platforms?",
      answer: "Different platforms have different best practices for subtitle overlay mockups: Facebook and Instagram favor vertical videos with text overlays, YouTube works well with traditional bottom positioning, TikTok users expect creative, dynamic text overlays, LinkedIn prefers professional, clean styling, and Twitter/X benefits from concise, impactful messaging. Adapt your overlay design to each platform's native video dimensions and user behavior. Consider creating platform-specific versions of your overlays to maximize engagement on each channel."
    }
  ],
  relatedResources: [
    {
      id: "creative-quality-grader",
      title: "Creative Quality Grader",
      relationshipDescription: "Analyze your video creative performance and get optimization recommendations",
      slug: "creative-quality-grader",
      fullRoute: "/resources/tools/analyzers/creative-quality-grader",
      resourceType: "tool",
      toolType: "analyzer"
    },
    {
      id: "video-dropoff-calculator",
      title: "Video Drop-off Rate Calculator",
      relationshipDescription: "Analyze video retention and optimize your content strategy",
      slug: "video-dropoff-calculator",
      fullRoute: "/resources/tools/calculators/video-dropoff-calculator",
      resourceType: "tool",
      toolType: "calculator"
    },
    {
      id: "meta-creative-specs-guide",
      title: "Meta Video Ad Creative Specs Guide",
      relationshipDescription: "Get exact video specifications for Facebook and Instagram ads",
      slug: "meta-ad-creative-video-specs-guide",
      fullRoute: "/resources/guides/meta-ad-creative-video-specs-guide",
      resourceType: "guide"
    }
  ]
}
